import React, { useEffect, useState } from 'react';

const BIRD_STATUS_OPTIONS = ['active', 'injured', 'lost', 'died'];
const BIRD_SEX_OPTIONS = ['Hen', 'Cock'];

const normalizeBirdCondition = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return BIRD_STATUS_OPTIONS.includes(normalized) ? normalized : 'active';
};

const normalizeBirdSexOption = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'h' || normalized === 'hen' || normalized === 'female' || normalized === 'f') return 'Hen';
  if (normalized === 'c' || normalized === 'cock' || normalized === 'male' || normalized === 'm') return 'Cock';
  return '';
};

const HandlersRequestsTable = ({ 
  requests, 
  allParticipants, 
  participant, 
  onApproveRequest, 
  onRejectClick, 
  onSaveBirdInfo,
  slotsLeft
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showBirdModal, setShowBirdModal] = useState(false);
  const [selectedRequestForBirds, setSelectedRequestForBirds] = useState(null);
  const [editBirds, setEditBirds] = useState([]);
  const [savingBirds, setSavingBirds] = useState(false);

  const toggleRowExpansion = (requestId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const getRequestStatus = (request) => String(
    request?.status
    ?? request?.Status
    ?? request?.requestStatus
    ?? request?.RequestStatus
    ?? ''
  ).trim().toLowerCase();

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || '').trim().toLowerCase();
    switch (normalizedStatus) {
      case 'pending':
        return <span className="badge bg-warning text-dark">Pending</span>;
      case 'approved':
        return <span className="badge bg-success">Approved</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      case 'deleted':
        return <span className="badge bg-secondary">Deleted</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const getBirdsInfo = (request) => {
    const requestBirdDetails = Array.isArray(request?.birdDetails) ? request.birdDetails : [];
    const birdsRequested = Number(request?.numberOfBirds || request?.birdsRequested || 0);

    if (requestBirdDetails.length > 0) {
      return requestBirdDetails;
    }

    const handlerBreeder = participant?.handlerInfo?.breeders?.find(b => b.breederId === request?.breederId);
    const fallbackBirds = Array.isArray(handlerBreeder?.birds) ? handlerBreeder.birds : [];

    if (fallbackBirds.length > 0) {
      return fallbackBirds;
    }

    return Array.from({ length: Math.max(0, birdsRequested) }, () => ({
      bandNumber: '',
      color: '',
      sex: '',
      birdStatus: 'active',
      status: 'placeholder'
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewBirds = (request) => {
    setSelectedRequestForBirds(request);
    setShowBirdModal(true);
  };

  useEffect(() => {
    if (!selectedRequestForBirds) {
      setEditBirds([]);
      return;
    }

    const birds = getBirdsInfo(selectedRequestForBirds);
    setEditBirds(birds.map((bird) => {
      const normalizedStatus = normalizeBirdCondition(bird?.birdStatus ?? bird?.status);
      return {
        ...bird,
        sex: normalizeBirdSexOption(bird?.sex ?? bird?.Sex ?? bird?.gender ?? bird?.Gender ?? bird?.birdSex ?? bird?.BirdSex),
        birdStatus: normalizedStatus,
        status: normalizedStatus,
      };
    }));
  }, [selectedRequestForBirds]);

  const handleBirdFieldChange = (idx, field, value) => {
    setEditBirds(prev => prev.map((bird, index) => {
      if (index !== idx) return bird;

      if (field === 'birdStatus') {
        const normalizedStatus = normalizeBirdCondition(value);
        return {
          ...bird,
          birdStatus: normalizedStatus,
          status: normalizedStatus,
        };
      }

      if (field === 'sex') {
        return {
          ...bird,
          sex: normalizeBirdSexOption(value),
        };
      }

      return bird;
    }));
  };

  const handleSaveBirdChanges = async () => {
    if (!selectedRequestForBirds?.id || typeof onSaveBirdInfo !== 'function') {
      return;
    }

    try {
      setSavingBirds(true);

      const normalizedBirds = editBirds.map((bird) => {
        const normalizedStatus = normalizeBirdCondition(bird?.birdStatus ?? bird?.status);
        return {
          ...bird,
          sex: normalizeBirdSexOption(bird?.sex),
          birdStatus: normalizedStatus,
          status: normalizedStatus,
        };
      });

      const result = await onSaveBirdInfo(selectedRequestForBirds.id, normalizedBirds);
      if (result?.success) {
        setShowBirdModal(false);
        setSelectedRequestForBirds(null);
      }
    } finally {
      setSavingBirds(false);
    }
  };

  const visibleRequests = requests.filter((request) => getRequestStatus(request) !== 'deleted');
  const pendingRequests = visibleRequests.filter((request) => getRequestStatus(request) === 'pending');
  const otherRequests = visibleRequests.filter((request) => getRequestStatus(request) !== 'pending');

  if (visibleRequests.length === 0) {
    return (
      <div className="card shadow-sm mb-4">
        <div className="card-body text-center py-5">
          <i className="bi bi-inbox text-muted" style={{ fontSize: '3rem' }}></i>
          <h6 className="text-muted mt-3">No requests received yet</h6>
          <p className="text-muted small">Handler requests will appear here when breeders send them to you.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Priority Alert for Pending Requests */}
      {pendingRequests.length > 0 && (
        <div className="alert alert-warning mb-4 shadow-sm" style={{ borderWidth: '2px' }}>
          <h6 className="alert-heading mb-3">
            <i className="bi bi-bell-fill me-2"></i>
            Action Required: {pendingRequests.length} Pending Request{pendingRequests.length > 1 ? 's' : ''}
          </h6>
          <p className="mb-0 small">
            Review and approve/reject pending requests below. Breeders are waiting for your response.
            <br></br>
            {pendingRequests.some(request => request.numberOfBirds > slotsLeft) ? <span style={{color: 'red'}}>If the Approve button is disabled, it means you don't have enough slots left to accommodate the request, increase your capacity to enable.</span> : ""}
            
          </p>
        </div>
      )}

      {/* Requests Table */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-list-ul me-2"></i>
            Handler Requests ({visibleRequests.length})
          </h5>
        </div>
        <div className="card-body p-0">
          {/* Desktop Table View */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 ps-3" style={{ width: '40px' }}></th>
                    <th className="border-0">Loft Name</th>
                    <th className="border-0">Breeder</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Birds</th>
                    <th className="border-0">Contact Info</th>
                    <th className="border-0" style={{ width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                {/* Render pending requests first */}
                {pendingRequests.map((request) => {
                  const breeder = allParticipants?.find(p => p.id === request.breederId);
                  const isExpanded = expandedRows.has(request.id);
                  const birds = getBirdsInfo(request);
                  
                  return (
                    <React.Fragment key={request.id}>
                      <tr className={request.status === 'pending' ? 'table-warning' : ''}>
                        <td className="ps-3">
                          <button
                            className="btn btn-sm btn-link text-decoration-none p-0"
                            onClick={() => toggleRowExpansion(request.id)}
                            title={isExpanded ? 'Collapse details' : 'Expand to see bird details'}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {breeder?.loftName || request.breederLoftName || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {breeder?.fullName || request.breederName || 'Unknown'}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(request.status)}
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {request.numberOfBirds}
                          </span>
                        </td>
                        <td>
                          <div className="small">
                            {breeder?.emailAddress && (
                              <div className="mb-1">
                                <i className="bi bi-envelope me-1"></i>
                                <a href={`mailto:${breeder.emailAddress}`} className="text-decoration-none">
                                  {breeder.emailAddress}
                                </a>
                              </div>
                            )}
                            {breeder?.mobile && (
                              <div>
                                <i className="bi bi-telephone me-1"></i>
                                <a href={`tel:${breeder.mobile}`} className="text-decoration-none">
                                  {breeder.mobile}
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div>Requested:</div>
                            <div className="text-muted">{formatDate(request.requestDate)}</div>
                            {request.approvedDate && (
                              <>
                                <div className="mt-1">Approved:</div>
                                <div className="text-muted">{formatDate(request.approvedDate)}</div>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {request.status === 'pending' && (
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => onApproveRequest(request.id)}
                                title={request.numberOfBirds > slotsLeft ? "Not enough slots left" : "Approve request"}
                                disabled={request.numberOfBirds > slotsLeft}
                              >Approve
                                <i className="bi bi-check"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => onRejectClick(request)}
                                title="Reject request"
                              >Reject
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      
                      {/* Expanded Row for Bird Details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="8" className="bg-light">
                            <div className="p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="bi bi-list-stars me-2"></i>
                                Bird Details ({birds.length} {birds.length === 1 ? 'bird' : 'birds'})
                              </h6>
                              {birds.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-sm table-striped mb-0">
                                    <thead>
                                      <tr>
                                        <th>Band Number</th>
                                        <th>Color</th>
                                        <th>Sex</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {birds.map((bird, index) => (
                                        <tr key={index}>
                                          <td>
                                            <code className="text-dark">{bird.bandNumber || 'N/A'}</code>
                                          </td>
                                          <td>{bird.color || 'N/A'}</td>
                                          <td>{bird.sex || 'N/A'}</td>
                                          <td>
                                            {bird.status === 'confirmed' ? (
                                              <span className="badge bg-success">Confirmed</span>
                                            ) : (
                                              <span className="badge bg-warning text-dark">Placeholder</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-muted text-center py-3">
                                  <i className="bi bi-info-circle me-2"></i>
                                  {request.status === 'approved' 
                                    ? 'Bird details will be available once breeder provides them.'
                                    : 'Bird details will be shown after request approval.'
                                  }
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                
                {/* Render other requests (approved/rejected) */}
                {otherRequests.map((request) => {
                  const breeder = allParticipants?.find(p => p.id === request.breederId);
                  const isExpanded = expandedRows.has(request.id);
                  const birds = getBirdsInfo(request);
                  
                  return (
                    <React.Fragment key={request.id}>
                      <tr>
                        <td className="ps-3">
                          <button
                            className="btn btn-sm btn-link text-decoration-none p-0"
                            onClick={() => toggleRowExpansion(request.id)}
                            title={isExpanded ? 'Collapse details' : 'Expand to see bird details'}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {breeder?.loftName || request.breederLoftName || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {breeder?.fullName || request.breederName || 'Unknown'}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(request.status)}
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {request.numberOfBirds}
                          </span>
                        </td>
                        <td>
                          <div className="small">
                            {breeder?.emailAddress && (
                              <div className="mb-1">
                                <i className="bi bi-envelope me-1"></i>
                                <a href={`mailto:${breeder.emailAddress}`} className="text-decoration-none">
                                  {breeder.emailAddress}
                                </a>
                              </div>
                            )}
                            {breeder?.mobile && (
                              <div>
                                <i className="bi bi-telephone me-1"></i>
                                <a href={`tel:${breeder.mobile}`} className="text-decoration-none">
                                  {breeder.mobile}
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {request.status === 'approved' && (
                            <button
                              className="btn btn-outline-info btn-sm"
                              onClick={() => handleViewBirds(request)}
                              title="View bird details"
                            >
                              <i className="bi bi-eye me-1"></i>
                              View Birds
                            </button>
                          )}
                        </td>
                      </tr>
                      
                      {/* Expanded Row for Bird Details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="8" className="bg-light">
                            <div className="p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="bi bi-list-stars me-2"></i>
                                Bird Details ({birds.length} {birds.length === 1 ? 'bird' : 'birds'})
                              </h6>
                              {birds.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-sm table-striped mb-0">
                                    <thead>
                                      <tr>
                                        <th>Band Number</th>
                                        <th>Color</th>
                                        <th>Sex</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {birds.map((bird, index) => (
                                        <tr key={index}>
                                          <td>
                                            <code className="text-dark">{bird.bandNumber || 'N/A'}</code>
                                          </td>
                                          <td>{bird.color || 'N/A'}</td>
                                          <td>{bird.sex || 'N/A'}</td>
                                          <td>
                                            {bird.status === 'confirmed' ? (
                                              <span className="badge bg-success">Confirmed</span>
                                            ) : (
                                              <span className="badge bg-warning text-dark">Placeholder</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-muted text-center py-3">
                                  <i className="bi bi-info-circle me-2"></i>
                                  {request.status === 'approved' 
                                    ? 'Bird details will be available once breeder provides them.'
                                    : 'Bird details will be shown after request approval.'
                                  }
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Mobile Card View */}
          <div className="d-md-none">
            {/* Render pending requests first */}
            {pendingRequests.map((request) => {
              const breeder = allParticipants?.find(p => p.id === request.breederId);
              const isExpanded = expandedRows.has(request.id);
              const birds = getBirdsInfo(request);
              
              return (
                <div key={request.id} className={`border-bottom ${request.status === 'pending' ? 'bg-warning bg-opacity-10' : ''}`}>
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-bold">{breeder?.loftName || request.breederLoftName || 'N/A'}</h6>
                        <div className="text-muted small">{breeder?.fullName || request.breederName || 'Unknown'}</div>
                      </div>
                      <div className="text-end">
                        {getStatusBadge(request.status)}
                        <div className="mt-1">
                          <span className="badge bg-primary">{request.numberOfBirds} birds</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    {(breeder?.emailAddress || breeder?.mobile) && (
                      <div className="mb-2">
                        {breeder?.emailAddress && (
                          <div className="small mb-1">
                            <i className="bi bi-envelope me-1"></i>
                            <a href={`mailto:${breeder.emailAddress}`} className="text-decoration-none">
                              {breeder.emailAddress}
                            </a>
                          </div>
                        )}
                        {breeder?.mobile && (
                          <div className="small">
                            <i className="bi bi-telephone me-1"></i>
                            <a href={`tel:${breeder.mobile}`} className="text-decoration-none">
                              {breeder.mobile}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Dates */}
                    <div className="small text-muted mb-3">
                      <div>Requested: {formatDate(request.requestDate)}</div>
                      {request.approvedDate && (
                        <div>Approved: {formatDate(request.approvedDate)}</div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-grow-1"
                        onClick={() => toggleRowExpansion(request.id)}
                        style={{ minHeight: '44px' }}
                      >
                        <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                        {isExpanded ? 'Hide' : 'Show'} Bird Details
                      </button>
                      
                      {request.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-success flex-grow-1"
                            onClick={() => onApproveRequest(request.id)}
                            style={{ minHeight: '44px' }}
                            title={request.numberOfBirds > slotsLeft ? "Not enough slots left" : "Approve request"}
                            disabled={request.numberOfBirds > slotsLeft}
                          >
                            <i className="bi bi-check me-1"></i>
                            Approve
                          </button>
                          <button
                            className="btn btn-danger flex-grow-1"
                            onClick={() => onRejectClick(request)}
                            style={{ minHeight: '44px' }}
                          >
                            <i className="bi bi-x me-1"></i>
                            Reject
                          </button>
                        </>
                      )}
                      
                      {request.status === 'approved' && (
                        <button
                          className="btn btn-outline-info flex-grow-1"
                          onClick={() => handleViewBirds(request)}
                          style={{ minHeight: '44px' }}
                        >
                          <i className="bi bi-eye me-1"></i>
                          View Birds
                        </button>
                      )}
                    </div>
                    
                    {/* Expanded Bird Details */}
                    {isExpanded && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="fw-bold mb-3">
                          <i className="bi bi-list-stars me-2"></i>
                          Bird Details ({birds.length} {birds.length === 1 ? 'bird' : 'birds'})
                        </h6>
                        {birds.length > 0 ? (
                          <div className="row g-2">
                            {birds.map((bird, index) => (
                              <div key={index} className="col-12">
                                <div className="card card-body py-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div className="fw-bold">
                                        <code className="text-dark">{bird.bandNumber || 'N/A'}</code>
                                      </div>
                                      <div className="small text-muted">
                                        {bird.color || 'N/A'} • {bird.sex || 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      {bird.status === 'confirmed' ? (
                                        <span className="badge bg-success">Confirmed</span>
                                      ) : (
                                        <span className="badge bg-warning text-dark">Placeholder</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted text-center py-3">
                            <i className="bi bi-info-circle me-2"></i>
                            {request.status === 'approved' 
                              ? 'Bird details will be available once breeder provides them.'
                              : 'Bird details will be shown after request approval.'
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Render other requests (approved/rejected) */}
            {otherRequests.map((request) => {
              const breeder = allParticipants?.find(p => p.id === request.breederId);
              const isExpanded = expandedRows.has(request.id);
              const birds = getBirdsInfo(request);
              
              return (
                <div key={request.id} className="border-bottom">
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-bold">{breeder?.loftName || request.breederLoftName || 'N/A'}</h6>
                        <div className="text-muted small">{breeder?.fullName || request.breederName || 'Unknown'}</div>
                      </div>
                      <div className="text-end">
                        {getStatusBadge(request.status)}
                        <div className="mt-1">
                          <span className="badge bg-primary">{request.numberOfBirds} birds</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    {(breeder?.emailAddress || breeder?.mobile) && (
                      <div className="mb-2">
                        {breeder?.emailAddress && (
                          <div className="small mb-1">
                            <i className="bi bi-envelope me-1"></i>
                            <a href={`mailto:${breeder.emailAddress}`} className="text-decoration-none">
                              {breeder.emailAddress}
                            </a>
                          </div>
                        )}
                        {breeder?.mobile && (
                          <div className="small">
                            <i className="bi bi-telephone me-1"></i>
                            <a href={`tel:${breeder.mobile}`} className="text-decoration-none">
                              {breeder.mobile}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Dates */}
                    <div className="small text-muted mb-3">
                      <div>Requested: {formatDate(request.requestDate)}</div>
                      {request.approvedDate && (
                        <div>Approved: {formatDate(request.approvedDate)}</div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-grow-1"
                        onClick={() => toggleRowExpansion(request.id)}
                        style={{ minHeight: '44px' }}
                      >
                        <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                        {isExpanded ? 'Hide' : 'Show'} Bird Details
                      </button>
                      
                      {request.status === 'approved' && (
                        <button
                          className="btn btn-outline-info flex-grow-1"
                          onClick={() => handleViewBirds(request)}
                          style={{ minHeight: '44px' }}
                        >
                          <i className="bi bi-eye me-1"></i>
                          View Birds
                        </button>
                      )}
                    </div>
                    
                    {/* Expanded Bird Details */}
                    {isExpanded && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="fw-bold mb-3">
                          <i className="bi bi-list-stars me-2"></i>
                          Bird Details ({birds.length} {birds.length === 1 ? 'bird' : 'birds'})
                        </h6>
                        {birds.length > 0 ? (
                          <div className="row g-2">
                            {birds.map((bird, index) => (
                              <div key={index} className="col-12">
                                <div className="card card-body py-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div className="fw-bold">
                                        <code className="text-dark">{bird.bandNumber || 'N/A'}</code>
                                      </div>
                                      <div className="small text-muted">
                                        {bird.color || 'N/A'} • {bird.sex || 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      {bird.status === 'confirmed' ? (
                                        <span className="badge bg-success">Confirmed</span>
                                      ) : (
                                        <span className="badge bg-warning text-dark">Placeholder</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted text-center py-3">
                            <i className="bi bi-info-circle me-2"></i>
                            {request.status === 'approved' 
                              ? 'Bird details will be available once breeder provides them.'
                              : 'Bird details will be shown after request approval.'
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bird Details Modal */}
      {showBirdModal && selectedRequestForBirds && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowBirdModal(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-eye me-2"></i>
                  Bird Details - {(() => {
                    const breeder = allParticipants?.find(p => p.id === selectedRequestForBirds.breederId);
                    return breeder ? breeder.fullName : 'Unknown Breeder';
                  })()}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowBirdModal(false)}
                ></button>
              </div>
              
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Request Status:</strong> {getStatusBadge(selectedRequestForBirds.status)}
                  </div>
                  <div className="col-md-6">
                    <strong>Birds Requested:</strong> {selectedRequestForBirds.numberOfBirds || selectedRequestForBirds.birdsRequested}
                  </div>
                  <div className="col-md-6">
                    <strong>Request Date:</strong> {formatDate(selectedRequestForBirds.requestDate)}
                  </div>
                  {selectedRequestForBirds.notes && (
                    <div className="col-12 mt-2">
                      <strong>Notes:</strong> <em>{selectedRequestForBirds.notes}</em>
                    </div>
                  )}
                </div>

                <hr />

                <h6 className="fw-bold mb-3">
                  <i className="bi bi-list-stars me-2"></i>
                  Bird Information
                </h6>
                <div className="small text-muted mb-3">
                  Only Sex and Bird Status can be updated by the handler.
                </div>
                
                {(() => {
                  const birds = editBirds;
                  
                  if (birds.length > 0) {
                    return (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead className="table-light">
                            <tr>
                              <th>Band Number</th>
                              <th>Color</th>
                              <th>Sex</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {birds.map((bird, index) => (
                              <tr key={index}>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={bird.bandNumber || '[To be added]'}
                                    disabled
                                    readOnly
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={bird.color || '[To be added]'}
                                    disabled
                                    readOnly
                                  />
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={normalizeBirdSexOption(bird.sex)}
                                    onChange={(event) => handleBirdFieldChange(index, 'sex', event.target.value)}
                                  >
                                    <option value="">Select sex</option>
                                    {BIRD_SEX_OPTIONS.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={normalizeBirdCondition(bird.birdStatus ?? bird.status)}
                                    onChange={(event) => handleBirdFieldChange(index, 'birdStatus', event.target.value)}
                                  >
                                    {BIRD_STATUS_OPTIONS.map((option) => (
                                      <option key={option} value={option}>
                                        {option.charAt(0).toUpperCase() + option.slice(1)}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>No bird details available yet.</strong>
                        <br />
                        The breeder will provide detailed bird information (band numbers, colors, etc.) before shipping.
                      </div>
                    );
                  }
                })()}
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveBirdChanges}
                  disabled={savingBirds || !selectedRequestForBirds?.id}
                >
                  {savingBirds ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-1"></i>
                      Save
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowBirdModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HandlersRequestsTable;